import * as React from 'react';
import { cn } from '@/lib/utils';

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  containerRef?: React.Ref<HTMLDivElement>;
  /** If true, vertical wheel scroll is converted to horizontal scroll on the container */
  horizontalWheelScroll?: boolean;
};

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerRef, horizontalWheelScroll, ...props }, ref) => (
    <TableContainer ref={containerRef} horizontalWheelScroll={horizontalWheelScroll}>
      <table
        ref={ref}
        data-tag="neeve-dls"
        className={cn('dls-w-full dls-body', className)}
        {...props}
      />
    </TableContainer>
  )
);
Table.displayName = 'Table';

type TableContainerProps = {
  children: React.ReactNode;
  className?: string;
  horizontalWheelScroll?: boolean;
};

const TableContainer = React.forwardRef<HTMLDivElement, TableContainerProps>(
  ({ children, className, horizontalWheelScroll }, forwardedRef) => {
    const innerRef = React.useRef<HTMLDivElement | null>(null);

    React.useImperativeHandle(forwardedRef, () => innerRef.current as HTMLDivElement);

    React.useEffect(() => {
      const el = innerRef.current;
      if (!el || !horizontalWheelScroll) return;

      const handleWheel = (event: WheelEvent) => {
        const { scrollWidth, clientWidth, scrollLeft } = el;
        if (scrollWidth <= clientWidth) return;

        const { deltaX, deltaY } = event;
        if (Math.abs(deltaX) > Math.abs(deltaY)) return;
        if (deltaY === 0) return;

        event.preventDefault();
        event.stopPropagation();

        const maxScrollLeft = scrollWidth - clientWidth;
        el.scrollLeft = Math.max(0, Math.min(scrollLeft + deltaY, maxScrollLeft));
      };

      el.addEventListener('wheel', handleWheel, { passive: false });
      return () => el.removeEventListener('wheel', handleWheel);
    }, [horizontalWheelScroll]);

    return (
      <div
        ref={innerRef}
        className={cn(
          'dls-w-full dls-overflow-x-auto dls-overflow-y-hidden dls-rounded-t-lg dls-border dls-border-border-primary dls-flex-1',
          className
        )}
        style={{ overscrollBehavior: horizontalWheelScroll ? 'none' : 'auto' }}
        data-tag="neeve-dls"
      >
        {children}
      </div>
    );
  }
);
TableContainer.displayName = 'TableContainer';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('dls-bg-background-primary', className)}
    data-tag="neeve-dls"
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('', className)} data-tag="neeve-dls" {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <tfoot data-tag="neeve-dls">
    <tr>
      <td colSpan={100} className={cn('dls-body dls-px-4 dls-h-12', className)}>
        {children}
      </td>
    </tr>
  </tfoot>
);
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn('dls-border-b dls-transition-colors', className)}
      data-tag="neeve-dls"
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'dls-text-left dls-align-middle dls-text-text-secondary dls-body-small [&:has([role=checkbox])]:dls-pr-0 [&>[role=checkbox]]:dls-translate-y-[2px]',
      className
    )}
    data-tag="neeve-dls"
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'dls-align-middle dls-body dls-min-w-[3rem] [&:has([role=checkbox])]:dls-pr-0 [&>[role=checkbox]]:dls-translate-y-[2px]',
      className
    )}
    data-tag="neeve-dls"
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('dls-mt-4 dls-text-sm dls-text-muted-foreground', className)} data-tag="neeve-dls" {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
